import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LoaderCircle } from "lucide-react";
import { LOCALES, LOCALE_NAMES, isLocale, type Locale } from "@shared/i18n";
import { setLocale, useLocale, useT } from "@shared/client/locale";
import { useSession } from "@shared/client/session";
import { updateLocale } from "@shared/lib/auth-client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { cn } from "@shared/lib/utils";

/** Once the session is known, the app speaks the account's language. */
export function LocaleSync() {
  const locale = useSession()?.user.locale;
  useEffect(() => {
    if (locale) setLocale(locale);
  }, [locale]);
  return null;
}

/**
 * Language picker. Signed in, the choice is saved on the account (emails and
 * agent output follow it) and every view is reloaded, since the API computes
 * some labels; as a guest (sign-in pages), it only switches the interface.
 */
export function LocaleSelect({ className, id }: { className?: string; id?: string }) {
  const locale = useLocale();
  const t = useT();
  const signedIn = Boolean(useSession());
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  async function choose(next: Locale) {
    if (!signedIn) {
      setLocale(next);
      return;
    }
    setBusy(true);
    try {
      await updateLocale(next);
      setLocale(next);
      await queryClient.invalidateQueries();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Select disabled={busy} onValueChange={(value) => isLocale(value) && void choose(value)} value={locale}>
        <SelectTrigger aria-label={t.common.language.label} className="h-9 w-[160px] text-[13px]" id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LOCALES.map((option) => (
            <SelectItem key={option} lang={option} value={option}>
              {LOCALE_NAMES[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {busy && <LoaderCircle aria-hidden="true" className="size-4 animate-spin text-muted-foreground" />}
    </div>
  );
}
